<template>
  <div>
    <v-form v-model="isFormValid" ref="form" v-if="entity">
      <h2>Ticket #{{ entity.ticket_number }}</h2>

      <!-- Customer -->
      <AdminFieldRelated
        :item="entity"
        label="Customer"
        :Model="OrderModel"
        :header="{
          value: 'customer.full_name',
          foreignModel: CustomerModel,
        }"
        @update="$emit('update', $event)"
      />

      <AdminFieldWrapper v-if="entity.is_gift">
        <div class="black--text">
          <v-icon class="mb-1" left small>mdi-gift-outline</v-icon>This is a
          Gift Order
        </div>
        <BtnBaseBlue @click="showGiftDialog = true" small outlined
          >Show Gift Details</BtnBaseBlue
        >
        <DialogGifteeDetails
          v-if="showGiftDialog"
          @close="showGiftDialog = false"
          @change="showGiftDialog = $event"
          :show="showGiftDialog"
          :order="entity"
        />
      </AdminFieldWrapper>

      <WithCustomerOrder
        :customer-id="entity.user_id"
        :order-id="entity.id"
        v-slot="{ order, loading }"
      >
        <transition name="fade-fast" mode="out-in">
          <div v-if="loading" class="text-center" key="loading-invoice">
            <h3 class="mt-2">Loading Order Items</h3>
            <v-progress-circular
              indeterminate
              class="mt-2"
              size="20"
              width="2"
            />
          </div>
          <AdminFieldLineItems v-else :order="order" />
        </transition>
      </WithCustomerOrder>

      <AdminFieldWrapper>
        <h3 class="mt-2 text-right"
          ><span class="mr-2">Total Cost: </span
          >{{ entity.grand_total_formatted }}</h3
        >
      </AdminFieldWrapper>

      <!-- Completed -->
      <AdminFieldCheckbox
        :Model="OrderModel"
        :id="entity.id"
        label="Completed"
        size="x-large"
        field-name="completed"
        @click="$emit('update', { completed: !entity.completed })"
        v-bind="globalFieldAttrs"
      />

      <!-- Paid -->
      <AdminFieldCheckbox
        :Model="OrderModel"
        :id="entity.id"
        label="Paid"
        size="x-large"
        field-name="paid"
        @click="$emit('update', { paid: !entity.paid })"
        v-bind="globalFieldAttrs"
      />

      <!-- Ready For Guest -->
      <AdminFieldCheckbox
        :Model="OrderModel"
        :id="entity.id"
        label="Ready For Guest"
        size="x-large"
        field-name="ready_for_guest"
        @click="$emit('update', { ready_for_guest: !entity.ready_for_guest })"
        v-bind="globalFieldAttrs"
      />

      <!-- Availability -->
      <AdminFieldRelated
        :item="entity"
        :Model="OrderModel"
        :header="{
          value: 'availability.name',
          foreignModel: AvailabilityModel,
        }"
        sort-key="start_time"
        label="Availability"
        @update="$emit('update', $event)"
      />

      <!-- Fulfillment Type -->
      <AdminFieldSingleSelect
        :value="entity.fulfillment_type"
        :Model="OrderModel"
        :items="getFulfillmentOptions"
        :id="entity.id"
        label="Fulfillment Type"
        field-name="fulfillment_type"
        @change="$emit('update', { fulfillment_type: $event })"
        invalid-tooltip="Please choose valid Fulfillment Type"
      />

      <!-- Fulfillment Slot -->
      <AdminFieldFulfillmentSlotPicker
        :fulfillment-type="entity.fulfillment_type"
        :value="entity.fulfillment_slot"
        field-name="fulfillment_slot"
        label="Fulfillment Slot"
        @change="$emit('update', { fulfillment_slot: $event })"
        :availability="entity.availability"
      />

      <!-- Created At -->
      <AdminFieldTimestampReadable
        :value="entity.time_ordered"
        label="Time Ordered"
      />

      <!-- Delivery Address -->
      <v-text-field
        v-if="entity.fulfillment_type === 'Delivery'"
        label="Delivery Address"
        :value="entity.delivery_address"
        @change="$emit('update', { delivery_address: $event })"
        v-bind="globalFieldAttrs"
      />

      <!-- Special Instructions -->
      <AdminFieldTextarea
        label="Order Notes"
        :value="entity.special_instructions"
        @change="$emit('update', { special_instructions: $event })"
        v-bind="globalFieldAttrs"
      />
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import OrderModel from '@/models/OrderModel'
import CustomerModel from '@/models/CustomerModel'
import AvailabilityModel from '@/models/AvailabilityModel'
import ValidationRules from '@/constants/ValidationRules'
import WithCustomerOrder from '@/components/DataProviders/WithCustomerOrder'

import _get from 'lodash/get'

import AdminFieldWrapper from '@/components/AdminFieldWrapper'
import AdminFieldCheckbox from '@/components/AdminFieldCheckbox'
import AdminFieldRelated from '@/components/AdminFieldRelated'
import AdminFieldTimestampReadable from '@/components/AdminFieldTimestampReadable'
import AdminFieldSingleSelect from '@/components/AdminFieldSingleSelect'
import AdminFieldTextarea from '@/components/AdminFieldTextarea'
import AdminFieldLineItems from '@/components/AdminFieldLineItems'
import AdminFieldFulfillmentSlotPicker from '@/components/AdminFieldFulfillmentSlotPicker'
import BtnBaseBlue from '@/components/BtnBaseBlue'
import DialogGifteeDetails from '@/components/DialogGifteeDetails.vue'

export default {
  name: 'AdminEditOrderForm',
  components: {
    WithCustomerOrder,
    AdminFieldWrapper,
    AdminFieldCheckbox,
    AdminFieldRelated,
    AdminFieldTimestampReadable,
    AdminFieldSingleSelect,
    AdminFieldTextarea,
    AdminFieldLineItems,
    AdminFieldFulfillmentSlotPicker,
    BtnBaseBlue,
    DialogGifteeDetails,
  },
  props: {
    entity: {
      type: OrderModel,
      require: true,
      default: () => new OrderModel(),
    },
  },
  data() {
    return {
      isFormValid: false,
      ValidationRules,
      showGiftDialog: false,
      OrderModel,
      CustomerModel,
      AvailabilityModel,
    }
  },
  methods: {
    _get,
  },
  mounted() {
    if (this.$refs.form) {
      this.$refs.form.validate()
    }
  },
  computed: {
    ...mapGetters('shop', ['getFulfillmentOptions', 'getFulfillmentSlotsList']),
    globalFieldAttrs() {
      const defaults = { outlined: true, dense: true }
      return { ...defaults }
    },
  },
  watch: {
    isFormValid(validity) {
      this.$emit('form:valid', validity)
    },
  },
}
</script>
